// @stories HIR-HR25, HIR-HR26, HIR-SYS2
// On the candidate panel (4 Nov 2026), read-only:
//   - the explained CV score: each component's contribution and why, the
//     coverage (a part the candidate's form never asked about is left out,
//     never a zero), the provisional weights, and their best-fit open post
//     when it is another (server: services/explainedCvScore.service.js);
//   - eligibility for the post: meets / not met / not stated per
//     requirement, with its authority, and the documents to ask for. Not
//     stated is never a rejection (services/eligibility.service.js);
//   - how to reach them: name, email, phone and city only — the blind wall.
import React, { useEffect, useState } from 'react';
import { ListChecks, ShieldCheck } from 'lucide-react';
import api from '../../../services/api';

const VERDICT_STYLE = {
  meets: 'bg-green-50 text-green-800 border-green-100',
  'not met': 'bg-red-50 text-red-800 border-red-100',
  'not stated': 'bg-yellow-50 text-yellow-800 border-yellow-100',
};

function useGet(url) {
  const [state, setState] = useState({ data: null, err: '' });
  useEffect(() => {
    let live = true;
    setState({ data: null, err: '' });
    api.get(url)
      .then((r) => { if (live) setState({ data: r.data?.data || null, err: '' }); })
      .catch((e) => { if (live) setState({ data: null, err: e.response?.data?.message || 'Could not read this just now.' }); });
    return () => { live = false; };
  }, [url]);
  return state;
}

export default function ExplainedCvScore({ resumeId }) {
  const score = useGet(`/cv-scoring/candidates/${resumeId}`);
  const elig = useGet(`/cv-scoring/candidates/${resumeId}/eligibility`);
  const contact = useGet(`/cv-scoring/candidates/${resumeId}/contact`);
  const s = score.data?.applied;

  return (
    <div className="mb-6 pt-4 border-t" data-testid="explained-cv-score">
      <h4 className="font-medium text-gray-900 flex items-center gap-1.5 mb-2">
        <ListChecks className="w-4 h-4 text-gray-600" /> Explained CV score
      </h4>
      {score.err && <p className="text-sm text-red-600">{score.err}</p>}
      {s && (
        <>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-lg font-semibold text-gray-900">
              {s.total == null ? '—' : s.total}<span className="text-sm font-normal text-gray-500"> / 100</span>
            </span>
            <span className="text-xs text-gray-600">covers {s.coverage} of 100</span>
            {score.data.ruleset?.provisional && (
              <span className="text-xs px-2 py-0.5 rounded border bg-yellow-50 text-yellow-800 border-yellow-100">Provisional weights</span>
            )}
          </div>
          <ul className="mt-2 text-xs text-gray-700 space-y-0.5">
            {s.components.map((c) => (
              <li key={c.code} className="flex justify-between gap-2">
                <span>{c.label}<span className="text-gray-500"> · {c.note}</span></span>
                <span className="text-gray-500 whitespace-nowrap">
                  {c.asked ? `${c.contribution} of ${c.appliedWeight}` : 'not asked'}
                </span>
              </li>
            ))}
          </ul>
          {score.data.bestFit && (
            <p className="text-xs text-blue-800 mt-2">
              Best fit: {score.data.bestFit.title} ({score.data.bestFit.total}, {score.data.bestFit.betterBy} more than this post).
            </p>
          )}
        </>
      )}

      {elig.data && (
        <div className="mt-4">
          <div className="flex items-center gap-2 text-sm text-gray-900">
            Eligibility
            <span className={`text-xs px-2 py-0.5 rounded border ${VERDICT_STYLE[elig.data.overall] || ''}`}>{elig.data.overall}</span>
          </div>
          {elig.data.reason && <p className="text-xs text-gray-600 mt-1">{elig.data.reason}</p>}
          <ul className="mt-1 text-xs text-gray-700 space-y-0.5">
            {(elig.data.requirements || []).map((r) => (
              <li key={r.code} className="flex justify-between gap-2">
                <span>{r.label}<span className="text-gray-500"> · {r.authority}</span></span>
                <span className={`whitespace-nowrap px-1.5 rounded border ${VERDICT_STYLE[r.verdict] || ''}`}>{r.verdict}</span>
              </li>
            ))}
          </ul>
          {(elig.data.chase || []).length > 0 && (
            <p className="text-xs text-yellow-800 mt-1">Ask for: {elig.data.chase.join(', ')}. Not a rejection.</p>
          )}
        </div>
      )}

      {contact.data && (
        <p className="text-xs text-gray-600 mt-4 flex items-center gap-1.5">
          <ShieldCheck className="w-3.5 h-3.5" />
          {[contact.data.name, contact.data.email, contact.data.phone, contact.data.city].filter(Boolean).join(' · ')}
        </p>
      )}
    </div>
  );
}
